import EmptyState from '@/components/EmptyState';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { TableState } from '@restaurant/api-client';
import { Block, BlockTitle, List, ListItem, Preloader } from 'framework7-react';
import { useTableStates } from '../hooks/useTableOrdering';
import { STATE_LABEL_KEY, STATE_STYLE, relTime } from '../lib/tableState';

type ViewMode = 'MAP' | 'LIST';

/**
 * Non-canvas table views. `LIST` is the dense scannable list (the workspace's
 * "List" toggle - best for long table counts / phones). `MAP` is the
 * zone-grouped card grid used as the fallback when there's no drawn floor
 * plan (SIMPLE table mode, or ADVANCED with nothing laid out yet) - the
 * drawn interactive plan is `FloorPlanLiveView`. Colour = live state; the
 * open-tab total and any pending service call surface on each row/card.
 */
export default function TableGridView({
  mode,
  activeZoneId,
  selectedId,
  onSelect,
}: {
  mode: ViewMode;
  /** When set, the view is scoped to one zone (the Tables tab's zone tab bar
   *  owns selection) - per-zone headings are dropped since the tab names it.
   *  `null` = the "No zone" bucket. Omit for the legacy grouped view. */
  activeZoneId?: string | null;
  selectedId: string | null;
  onSelect: (table: TableState) => void;
}) {
  const t = useT().tableOrdering.tableGrid;
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const { data, isLoading, error } = useTableStates();

  if (isLoading) {
    return (
      <Block style={{ textAlign: 'center', paddingTop: 48 }}>
        <Preloader size={32} />
      </Block>
    );
  }
  if (error) {
    return (
      <EmptyState
        icon="exclamationmark_triangle"
        title={t.couldntLoadTables}
        description={t.tryAgain}
      />
    );
  }
  const all = data ?? [];
  if (all.length === 0) {
    return <EmptyState icon="square_grid_2x2" title={t.noTables} description={t.noTablesDesc} />;
  }

  // Scoped to one zone (tab bar owns selection) → drop per-zone headings.
  const scoped = activeZoneId !== undefined;
  const tables = scoped ? all.filter((tb) => (tb.zoneId ?? null) === activeZoneId) : all;
  if (tables.length === 0) {
    return (
      <EmptyState icon="square_grid_2x2" title={t.noTablesZone} description={t.noTablesZoneDesc} />
    );
  }
  const showZoneTitles = !scoped;

  // Group by zone, stable order, unzoned last.
  const zones = new Map<string, { label: string; rows: TableState[] }>();
  for (const tb of tables) {
    const key = tb.zoneId ?? '__none';
    if (!zones.has(key)) {
      zones.set(key, { label: tb.zoneLabel ?? t.other, rows: [] });
    }
    zones.get(key)!.rows.push(tb);
  }

  if (mode === 'LIST') {
    return (
      <>
        {[...zones.values()].map((zone, i) => (
          <div key={zone.label + i}>
            {showZoneTitles ? (
              <BlockTitle medium style={{ marginTop: i === 0 ? 16 : 12 }}>
                {t.zoneCount(zone.label, zone.rows.length)}
              </BlockTitle>
            ) : (
              i === 0 && <div style={{ height: 12 }} />
            )}
            <List dividers strongIos insetIos style={{ marginBottom: 8 }}>
              {zone.rows.map((tb) => {
                const s = STATE_STYLE[tb.state];
                const sub =
                  tb.state === 'OCCUPIED'
                    ? t.occupiedSub(
                        tb.roundCount,
                        formatMoney(tb.totalCents, currency),
                        tb.pendingServiceRequests,
                      )
                    : tb.state === 'RESERVED' && tb.nextReservationAt
                      ? t.reservedAt(
                          new Date(tb.nextReservationAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          }),
                        )
                      : t.seatsCount(tb.seats);
                return (
                  <ListItem
                    key={tb.tableId}
                    link="#"
                    noChevron
                    title={tb.label}
                    footer={sub}
                    onClick={() => onSelect(tb)}
                    style={
                      tb.tableId === selectedId
                        ? { backgroundColor: 'var(--f7-theme-color-tint)' }
                        : undefined
                    }
                  >
                    <div slot="after" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {tb.pendingServiceRequests > 0 && (
                        <i className="f7-icons" style={{ fontSize: 16, color: '#ff3b30' }}>
                          bell_fill
                        </i>
                      )}
                      <span
                        style={{
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          background: s.dot,
                          display: 'inline-block',
                        }}
                      />
                    </div>
                  </ListItem>
                );
              })}
            </List>
          </div>
        ))}
        <div style={{ height: 24 }} />
      </>
    );
  }

  return (
    <>
      {[...zones.values()].map((zone, i) => (
        <div key={zone.label + i}>
          {showZoneTitles ? (
            <BlockTitle medium style={{ marginTop: i === 0 ? 16 : 12 }}>
              {zone.label}
            </BlockTitle>
          ) : (
            i === 0 && <div style={{ height: 12 }} />
          )}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
              gap: 10,
              padding: '0 16px 4px',
            }}
          >
            {zone.rows.map((tb) => {
              const s = STATE_STYLE[tb.state];
              const active = tb.tableId === selectedId;
              return (
                <button
                  key={tb.tableId}
                  type="button"
                  onClick={() => onSelect(tb)}
                  style={{
                    position: 'relative',
                    textAlign: 'left',
                    border: active
                      ? '2px solid var(--f7-theme-color)'
                      : '1px solid var(--f7-list-item-border-color, rgba(0,0,0,0.12))',
                    background: active ? 'var(--f7-theme-color-tint)' : s.tint,
                    borderRadius: 14,
                    padding: '12px 12px 14px',
                    minHeight: 96,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontWeight: 650, fontSize: 16 }}>{tb.label}</span>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: s.dot,
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 12, color: '#8e8e93' }}>
                    {t[STATE_LABEL_KEY[tb.state]]} · {t.seatsCount(tb.seats)}
                  </span>
                  {tb.state === 'OCCUPIED' && (
                    <span style={{ marginTop: 'auto', fontSize: 13, fontWeight: 600 }}>
                      {formatMoney(tb.totalCents, currency)}
                      <span style={{ color: '#8e8e93', fontWeight: 400 }}>
                        {' '}
                        · {t.roundsAbbrev(tb.roundCount)} · {relTime(tb.openedAt)}
                      </span>
                    </span>
                  )}
                  {tb.state === 'RESERVED' && tb.nextReservationAt && (
                    <span style={{ marginTop: 'auto', fontSize: 12, color: '#ff9500' }}>
                      {new Date(tb.nextReservationAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  )}
                  {tb.pendingServiceRequests > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: -7,
                        left: -7,
                        background: '#ff3b30',
                        color: '#fff',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {tb.pendingServiceRequests}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div style={{ height: 24 }} />
    </>
  );
}
